import { useRef } from 'react';
import type { ReactNode } from 'react';
import { Outlet } from 'react-router';

import { useRefreshOnResume } from '@/features/refresh-on-resume';
import { OVERLAY_ROOT_ID } from '@/shared/ui';

import { useAppScale } from '../model/use-app-scale';
import { useOverlayOffsets } from '../model/use-overlay-offsets';
import { useScrollToTop } from '../model/use-scroll-to-top';

import { AppHeader } from './AppHeader';
import { BottomBar } from './BottomBar';
import styles from './AppLayout.module.scss';

interface AppLayoutProps {
  /**
   * Полноэкранные оверлеи поверх хэдера/контента/нижнего бара (например,
   * WelcomeModal). Принимаются снаружи, а не импортируются виджетом
   * напрямую — соседние виджеты друг друга импортировать не могут (FSD),
   * а `position: absolute` оверлея должен считаться от `.layout`, поэтому
   * рендериться оверлей обязан внутри него.
   */
  children?: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const mainRef = useRef<HTMLElement>(null);

  useRefreshOnResume();
  useAppScale();
  useOverlayOffsets();
  useScrollToTop(mainRef);

  return (
    <div className={styles.layout}>
      <AppHeader />

      <main className={styles.main} ref={mainRef} data-testid="app-scroll">
        <Outlet />
      </main>

      <BottomBar />

      {/* Оверлеи из глубины страниц (шторка Прогноза, статистика,
          Дерби) выходят сюда порталом — сиблингом .main, а не его потомком:
          в WebKit прокручиваемый контейнер не выпускает z-index наружу,
          и хэдер закрывал крестик шторки. См. OverlayPortal */}
      <div id={OVERLAY_ROOT_ID} data-testid="app-overlay-root" />

      {children}
    </div>
  );
}
